import { backend_source, chat_page_limit } from "./bolide_assistant_constants";

export default {
  async load_conversations(preferred_active_id = null) {
    this.is_loading_chat_history = true;
    this.chat_history_next_cursor = null;

    try {
      const page = await this.fetch_backend_conversation_page();
      this.chat_history_next_cursor = page.next_cursor;
      this.has_loaded_backend_history = true;
      this.chat_history_load_error = "";
      this.conversations = page.conversations.length
        ? this.merge_backend_conversation_page(page.conversations)
        : [this.create_conversation()];
    } catch (error) {
      this.has_loaded_backend_history = false;
      this.chat_history_load_error = error?.message || "VSI Assist history is unavailable.";
      this.log_chat_history_error(error);
      this.conversations = [this.create_conversation()];
    } finally {
      this.is_loading_chat_history = false;
    }

    this.restore_active_conversation(preferred_active_id);
    await this.ensure_active_conversation_messages();

    if (this.should_start_new_after_history_load && this.is_open) {
      this.start_fresh_open_conversation();
    }
  },

  async load_more_conversations() {
    if (!this.chat_history_next_cursor || this.is_loading_chat_history) return false;
    this.is_loading_chat_history = true;

    try {
      const page = await this.fetch_backend_conversation_page({
        cursor: this.chat_history_next_cursor,
      });
      this.chat_history_next_cursor = page.next_cursor;
      this.conversations = this.merge_backend_conversation_page(page.conversations, {
        append: true,
      });
      return true;
    } catch {
      return false;
    } finally {
      this.is_loading_chat_history = false;
    }
  },

  async refresh_conversations_from_backend(preferred_active_id = null) {
    const page = await this.fetch_backend_conversation_page();
    this.chat_history_next_cursor = page.next_cursor;
    if (!page.conversations.length) return false;

    this.conversations = this.merge_backend_conversation_page(page.conversations);
    this.restore_active_conversation(preferred_active_id || this.active_conversation_id);
    await this.load_conversation_messages(this.active_conversation_id, { replace: true });
    return true;
  },

  refresh_conversations_from_backend_safely(preferred_active_id = null) {
    return this.refresh_conversations_from_backend(preferred_active_id).catch(() => false);
  },

  retry_load_conversations_if_needed() {
    if (this.has_loaded_backend_history || this.is_loading_chat_history) return false;
    return this.load_conversations(this.active_conversation_id);
  },

  log_chat_history_error(error) {
    if (typeof console === "undefined" || !console.warn) return;
    console.warn("VSI Assist history could not be loaded.", error);
  },

  async fetch_backend_conversation_page(params = {}) {
    let page;
    try {
      page = await this.fetch_assist_chat_summaries({
        limit: chat_page_limit,
        ...params,
      });
    } catch (error) {
      if (!this.should_use_history_summary_fallback(error)) throw error;
      return this.fetch_backend_conversation_page_from_history(params);
    }
    return ({
      conversations: page.results.map((summary) => this.create_backend_conversation(summary)),
      next_cursor: page.next_cursor || null,
    });
  },

  should_use_history_summary_fallback(error) {
    return [404, 405].includes(Number(error?.status));
  },

  async fetch_backend_conversation_page_from_history(params = {}) {
    const page = await this.fetch_assist_history({
      limit: chat_page_limit,
      ...params,
    });
    return ({
      conversations: this.history_records_to_conversation_summaries(page.results)
        .map((summary) => this.create_backend_conversation(summary)),
      next_cursor: page.next_cursor || null,
    });
  },

  history_records_to_conversation_summaries(records) {
    const summaries_by_chat = new Map();
    (records || []).forEach((record) => {
      const chat_id = String(record.chat_id || "default");
      const existing = summaries_by_chat.get(chat_id);
      const record_updated_at = record.completed_at || record.created_at;
      const record_updated_at_value = this.get_timestamp_sort_value(record_updated_at);

      if (!existing) {
        summaries_by_chat.set(chat_id, this.create_conversation_summary_from_record(record));
        return;
      }

      existing.message_count += 1;
      if (record_updated_at_value > existing.updated_at_value) {
        existing.updated_at = record_updated_at;
        existing.updated_at_value = record_updated_at_value;
        existing.preview = this.get_history_record_assistant_text(record)
          || record.query_text
          || existing.preview;
        existing.status = record.status || existing.status;
        existing.latest_query_id = record.id || existing.latest_query_id;
      }

      const record_created_at_value = this.get_timestamp_sort_value(record.created_at);
      if (record_created_at_value < existing.created_at_value) {
        existing.created_at = record.created_at;
        existing.created_at_value = record_created_at_value;
        existing.title = record.query_text || existing.title;
      }
    });

    // created_at_value / updated_at_value are only used for sorting; don't expose them
    return [...summaries_by_chat.values()].map((summary) => {
      const public_summary = Object.assign({}, summary);
      delete public_summary.created_at_value;
      delete public_summary.updated_at_value;
      return public_summary;
    });
  },

  create_conversation_summary_from_record(record) {
    const updated_at = record.completed_at || record.created_at;
    return ({
      chat_id: String(record.chat_id || "default"),
      title: record.query_text || "New conversation",
      preview: this.get_history_record_assistant_text(record) || record.query_text || "",
      status: record.status || "",
      message_count: 1,
      created_at: record.created_at,
      updated_at,
      latest_query_id: record.id || null,
      created_at_value: this.get_timestamp_sort_value(record.created_at),
      updated_at_value: this.get_timestamp_sort_value(updated_at),
    });
  },

  merge_backend_conversation_page(backend_conversations, options = {}) {
    const incoming_ids = new Set(backend_conversations.map((conversation) => conversation.id));
    const existing_by_id = new Map(
      this.conversations.map((conversation) => [conversation.id, conversation])
    );
    const merged_incoming = backend_conversations.map((conversation) => (
      this.merge_existing_conversation_state(existing_by_id.get(conversation.id), conversation)
    ));

    if (options.append) {
      const existing_conversations = this.conversations.filter(
        (conversation) => !incoming_ids.has(conversation.id)
      );
      return [...existing_conversations, ...merged_incoming];
    }

    const local_drafts = this.conversations.filter((conversation) => (
      conversation.source !== backend_source && !incoming_ids.has(conversation.id)
    ));
    return [...local_drafts, ...merged_incoming];
  },

  merge_existing_conversation_state(existing_conversation, incoming_conversation) {
    if (!existing_conversation) return incoming_conversation;
    const merged_conversation = this.merge_conversation_summary(
      existing_conversation,
      incoming_conversation
    );
    return ({
      ...merged_conversation,
      messages: existing_conversation.messages?.length
        ? existing_conversation.messages
        : merged_conversation.messages,
      messages_loaded: existing_conversation.messages_loaded
        || merged_conversation.messages_loaded,
      messages_next_cursor: existing_conversation.messages_next_cursor
        || merged_conversation.messages_next_cursor,
      is_loading_messages: false,
    });
  },

  merge_conversation_summary(existing_conversation, incoming_conversation) {
    const existing_updated_at = this.get_timestamp_sort_value(existing_conversation.updated_at);
    const incoming_updated_at = this.get_timestamp_sort_value(incoming_conversation.updated_at);
    const latest_summary = existing_updated_at > incoming_updated_at
      ? existing_conversation
      : incoming_conversation;
    const existing_created_at = this.get_timestamp_sort_value(existing_conversation.created_at);
    const incoming_created_at = this.get_timestamp_sort_value(incoming_conversation.created_at);
    const earliest_summary = existing_created_at <= incoming_created_at
      ? existing_conversation
      : incoming_conversation;

    return ({
      ...incoming_conversation,
      title: earliest_summary.title || incoming_conversation.title,
      preview: latest_summary.preview || incoming_conversation.preview,
      created_at: earliest_summary.created_at || incoming_conversation.created_at,
      updated_at: latest_summary.updated_at || incoming_conversation.updated_at,
      message_count: Math.max(
        existing_conversation.message_count || 0,
        incoming_conversation.message_count || 0
      ),
    });
  },
};
